import * as React from 'react'

import type moment from 'moment'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import useFetchDefaultShifts from 'modules/shift_management/components/ShiftManagement/MainPage/useFetchDefaultShifts'
import { useAddUnassignedDefaultShifts } from 'modules/shift_management/hooks/useAddUnassignedDefaultShifts'
import { useSelectionDispatch } from 'modules/shift_management/hooks/useSelection'
import UnassignedShiftForm from 'modules/shift_management/lib/forms/UnassignedShiftForm'
import type DefaultShift from 'modules/shift_management/store/internal/models/DefaultShift'

// TODO: Try to merge this with useDefaultShiftForms()
const useUnassignedDefaultShiftForms = () => {
  const dispatchSelection = useSelectionDispatch()

  const { defaultShifts, loading } = useFetchDefaultShifts({ fromUnassigned: true })

  const initializeForms = useUnsafeCallback(() => {
    return defaultShifts.active.map((defaultShift: DefaultShift) => {
      const { id, title, startAt, endAt, overnightShift, color, locationId, workAreaId, notes } =
        defaultShift

      return new UnassignedShiftForm({
        id,
        title,
        start_at: startAt,
        end_at: endAt,
        extra_hours: false,
        color,
        overnight_shift: overnightShift,
        location_id: locationId,
        work_area_id: workAreaId,
        notes,
        all_company: false,
        staff_requirements: [
          { job_catalog_roles_ids: [], job_catalog_tree_role_uuids: [], staff_count: 1 },
        ],
      })
    })
  }, [defaultShifts])

  const [forms, setForms] = React.useState<UnassignedShiftForm[]>(initializeForms())

  const onToggleShift = (id: string) => {
    const form = forms.find((form) => form.get('id').value === id)

    if (!form) throw 'Could not find form'

    form.get('is_selected').set(!form.get('is_selected').value)
  }

  const useSubmitUnassignedDefaultShifts = ({
    startDate,
    endDate,
    staffRequirements,
  }: {
    startDate: moment.Moment
    endDate: moment.Moment
    staffRequirements: Array<{
      job_catalog_roles_ids: string[]
      job_catalog_tree_role_uuids: string[]
      staff_count: number
    }>
  }) => {
    const { onSubmit, submitting } = useAddUnassignedDefaultShifts({
      startDate,
      endDate,
      staffRequirements: staffRequirements,
      forms: forms.filter((form) => form.get('is_selected').value),
      onSuccess: () => {
        dispatchSelection({
          type: 'CLEAR_SELECTION',
        })
      },
    })

    return {
      onSubmit,
      submitting,
    }
  }

  useUnsafeEffect(() => {
    if (loading) return
    setForms(initializeForms())
  }, [defaultShifts, loading, initializeForms])

  return {
    forms,
    setForms,
    onToggleShift,
    useSubmitUnassignedDefaultShifts,
  }
}

export default useUnassignedDefaultShiftForms
